import { useEventListener, useMutationObserver } from "@vueuse/core";
import { onMounted, onScopeDispose, toValue, watch } from "vue";
import { useHostElement } from "#ce/composables/useHostElement";
import { MOUNT_READY_EVENT, MOUNT_READY_MARKER, LOAD_FAILED_EVENT } from "#ce/framework/mount-ready";
import { CONTRIBUTION_REFERENCE_ATTRIBUTE } from "#ce/shared/finder-contribution-protocol";
import { FINDER_FILTER_BAR_TAG } from "#ce/shared/finder-filter-protocol";

/**
 * Connect an owner's reactive description to a bar in the same DOM tree. Missing
 * targets are observed only for this Vue scope's lifetime, without pending promises.
 *
 * @param {object} options
 * @param {import('vue').MaybeRefOrGetter<string | HTMLElement | undefined>} options.target
 * @param {() => import('#ce/shared/finder-contribution-protocol').FinderContribution} options.describe
 */
export function useFinderContribution({ target, describe }) {
  const host = useHostElement();
  const lifetime = new AbortController();
  let descriptor;
  let registeredDescriptor;
  let connection;
  let bar;
  let root;
  let stopMutations;
  let stopReadinessListeners;
  let mounted = false;
  let queued = false;
  let duplicateReference;

  function disconnect() {
    connection?.disconnect();
    connection = undefined;
    registeredDescriptor = undefined;
  }

  function stopObserving() {
    stopMutations?.();
    stopMutations = undefined;
    stopReadinessListeners?.();
    stopReadinessListeners = undefined;
    root = undefined;
  }

  function schedule() {
    if (queued || lifetime.signal.aborted) {
      return;
    }
    queued = true;
    queueMicrotask(reconcile);
  }

  function observe(tree) {
    if (tree === root) {
      return;
    }
    stopObserving();
    root = tree;
    const { stop } = useMutationObserver(root, schedule, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [CONTRIBUTION_REFERENCE_ATTRIBUTE, MOUNT_READY_MARKER],
    });
    stopMutations = stop;
    stopReadinessListeners = useEventListener(root, [MOUNT_READY_EVENT, LOAD_FAILED_EVENT], schedule, true);
  }

  function resolveTarget(reference) {
    if (typeof reference !== "string") {
      return reference?.getRootNode() === root ? reference : undefined;
    }
    return resolveNamedTarget(reference);
  }

  function resolveNamedTarget(reference) {
    const matches = findNamedBars(root, reference);
    if (matches.length <= 1) {
      duplicateReference = undefined;
      return matches[0];
    }
    if (duplicateReference !== reference) {
      console.error(`Duplicate finder contribution reference: ${reference}`);
    }
    duplicateReference = reference;
  }

  function reconcile() {
    queued = false;
    if (!mounted || lifetime.signal.aborted) {
      return;
    }
    const reference = toValue(target);
    if (!reference) {
      clearTarget();
      return;
    }
    const tree = host?.getRootNode();
    // A synchronous move can temporarily make the host its own root. Membership
    // is reconciled from the settled tree, never from individual removal records.
    if (!tree || (tree.nodeType !== Node.DOCUMENT_NODE && !tree.host)) {
      return;
    }
    observe(tree);
    const next = resolveTarget(reference);
    if (bar !== next || connection?.closed || !next?.hasAttribute(MOUNT_READY_MARKER)) {
      disconnect();
      bar = next;
    }
    syncDescription();
  }

  function syncDescription() {
    if (!descriptor || !bar?.hasAttribute(MOUNT_READY_MARKER)) {
      return;
    }
    if (connection && !connection.closed && sameMetadata(registeredDescriptor, descriptor)) {
      connection.updateState(descriptor.state);
      return;
    }
    // Connecting first lets same-id replacement retain its position in the menu.
    const previous = connection;
    connection = bar.connectContribution(descriptor, { signal: lifetime.signal });
    registeredDescriptor = descriptor;
    previous?.disconnect();
  }

  function updateDescription(value) {
    descriptor = { ...value, state: { ...value.state }, actions: { ...value.actions } };
    syncDescription();
  }

  function start() {
    mounted = true;
    reconcile();
  }

  function clearTarget() {
    disconnect();
    stopObserving();
    bar = undefined;
  }

  function dispose() {
    lifetime.abort();
    clearTarget();
  }

  watch(describe, updateDescription, { immediate: true, deep: true });
  watch(() => toValue(target), schedule);
  onMounted(start);
  onScopeDispose(dispose);
}

function sameMetadata(a, b) {
  return (
    a?.id === b.id &&
    a.label === b.label &&
    a.icon === b.icon &&
    a.actions.clear === b.actions.clear &&
    a.actions.reveal === b.actions.reveal
  );
}

function findNamedBars(root, reference) {
  const candidates = root.querySelectorAll(`${FINDER_FILTER_BAR_TAG}[${CONTRIBUTION_REFERENCE_ATTRIBUTE}]`);
  const matches = [];
  for (const element of candidates) {
    if (element.getAttribute(CONTRIBUTION_REFERENCE_ATTRIBUTE) === reference) {
      matches.push(element);
    }
  }
  return matches;
}
